import moment from 'moment';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Subscription as RxSubscription } from 'rxjs';
import { IOrder } from '../interfaces/order';
import { OrderDataSourceConfig } from '../interfaces/order-data-source-config';
import { OrderApiService } from './order-api-service';
// import {OrderApiService} from './order-api-service';

@Injectable()
export class OrderDataSource extends DataSource<IOrder> {
  public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public PageEvent: BehaviorSubject<PageEvent | null> = new BehaviorSubject(null);
  public totalTickets: BehaviorSubject<number> = new BehaviorSubject<number>(
    null
  );
  public filter: BehaviorSubject<{
    email?: string;
  }> = new BehaviorSubject({});

  private Orders: BehaviorSubject<IOrder[]> = new BehaviorSubject([]);

  private Subscriptions: RxSubscription[] = [];
  private latestRequest = 0;

  constructor(
    protected OrderApi: OrderApiService // protected DataSourceConfig: OrderDataSourceConfig,
  ) {
    super();
  }

  public getConfig() {
    // return this.DataSourceConfig;
  }

  public async load(id: number, pageIndex = 0): Promise<IOrder[]> {
    const request = ++this.latestRequest;
    this.loading.next(true);
    try {
      const result = await this.OrderApi.list(id, pageIndex);
      const orders = this.formatOrders(result.data);

      if (request === this.latestRequest) {
        this.totalTickets.next(
          orders.reduce(
            (total, order) => total + (order.order_details.ticketCount || 0),
            0,
          ),
        );
        this.PageEvent.next(result.pageEvent);
        this.Orders.next(orders);
      }

      return orders;
    } finally {
      if (request === this.latestRequest) {
        this.loading.next(false);
      }
    }
  }

  public async fetchAllGuestListPages(id: number): Promise<IOrder[]> {
    const firstPage = await this.OrderApi.list(id, 0);
    const { pageSize, length } = firstPage.pageEvent;
    const pageCount = Math.ceil(length / pageSize);
    const pages: IOrder[][] = Array(pageCount);

    pages[0] = firstPage.data;
    let nextPageIndex = 1;
    const workerCount = Math.min(4, Math.max(0, pageCount - 1));

    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        while (nextPageIndex < pageCount) {
          const pageIndex = nextPageIndex++;
          const page = await this.OrderApi.list(id, pageIndex);
          pages[pageIndex] = page.data;
        }
      }),
    );

    return this.formatOrders(pages.flat());
  }

  public setFilter(key: string, value: string) {
    const filter = this.filter.value;

    if (value) {
      filter[key] = value;
    } else {
      filter.hasOwnProperty(key) && delete filter[key];
    }

    this.filter.next(filter);
  }

  public refund(orderId: string, notes: string) {
    return new Promise<void>((resolve, reject) => {
      this.OrderApi.refund(orderId, notes).then(
        () => resolve(),
        (error: string) => reject(error)
      );
    });
  }

  public notes(orderId: string, notes: string) {
    return new Promise<void>((resolve, reject) => {
      this.OrderApi.notes(orderId, notes).then(
        () => resolve(),
        (error: string) => reject(error)
      );
    });
  }

  public handleError(error: unknown) {
    return this.OrderApi.handleError(error);
  }

  public connect(collectionViewer?: CollectionViewer) {
    return this.Orders;
  }

  public disconnect(collectionViewer?: CollectionViewer) {
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }

  private statusIconMap(status: string) {
    const statuses = {
      authorised: 'done',
      cleansed: 'soap',
      complete: 'update',
      refunded: 'close',
    };

    return statuses[status] || '';
  }

  private formatOrders(orders: IOrder[]): IOrder[] {
    return orders.map((order) => {
      const dateTime = moment(order.purchase_date);

      return {
        ...order,
        name: order.first_name + ' ' + order.last_name,
        phone: order.order_details.billingAddress?.phone,
        total_tickets: order.order_details.ticketCount,
        icon: this.statusIconMap(order.status),
        date: dateTime.format('YYYY-MM-DD'),
        time: dateTime.format('HH:mm'),
      };
    });
  }
}
